import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { db } from '../firebase';
import { DynamicPrompt } from '../types';

// Cache per i prompt per evitare troppe chiamate a Firestore
let promptsCache: Record<string, DynamicPrompt> = {};
let lastCacheUpdate = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minuti
const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};

const normalizeKnowledgeItems = (items: any): DynamicPrompt['knowledgeItems'] => {
  if (!Array.isArray(items)) return [];
  return items
    .filter((item) => item && typeof item === 'object')
    .map((item, index) => {
      const sourceType: 'manual' | 'file' = item.sourceType === 'file' ? 'file' : 'manual';
      return {
        id: String(item.id || `k_${index}`),
        title: String(item.title || 'Contenuto'),
        category: (item.category || 'documento') as any,
        content: String(item.content || ''),
        tags: Array.isArray(item.tags) ? item.tags.map((tag: any) => String(tag)) : [],
        sourceType,
        sourceName: item.sourceName ? String(item.sourceName) : undefined,
        createdAt: item.createdAt ? String(item.createdAt) : undefined
      };
    })
    .filter((item) => item.content.trim().length > 0);
};

// Funzione per recuperare tutti i prompt da Firestore
export async function getPromptsFromFirestore(): Promise<Record<string, DynamicPrompt>> {
  try {
    const now = Date.now();
    
    // Verifica se la cache è ancora valida
    if (promptsCache && Object.keys(promptsCache).length > 0 && (now - lastCacheUpdate) < CACHE_DURATION) {
      logDebug('Using cached prompts');
      return promptsCache;
    }

    logDebug('Fetching prompts from Firestore...');
    const promptsCollection = collection(db, 'prompts');
    const snapshot = await getDocs(promptsCollection);
    
    const prompts: Record<string, DynamicPrompt> = {};
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      prompts[doc.id] = {
        id: doc.id,
        geniusLociId: data.geniusLociId || doc.id,
        initialMessage: data.initialMessage || '',
        context: data.context || '',
        voice: data.voice || '',
        specialties: data.specialties || [],
        bio: data.bio || '',
        quote: data.quote || '',
        personality: data.personality || '',
        avatar: data.avatar || '',
        disableGlobalConcise: data.disableGlobalConcise === true,
        guidelines: data.guidelines || {},
        knowledgeItems: normalizeKnowledgeItems(data.knowledgeItems),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      };
    });

    // Aggiorna la cache
    promptsCache = prompts;
    lastCacheUpdate = now;
    
    logDebug(`Loaded ${Object.keys(prompts).length} prompts from Firestore`);
    return prompts;
    
  } catch (error) {
    console.error('Error fetching prompts from Firestore:', error);
    return {};
  }
}

// Funzione per recuperare un singolo prompt da Firestore
export async function getSinglePromptFromFirestore(geniusLociId: string): Promise<DynamicPrompt | null> {
  try {
    // Prima controlla la cache
    if (promptsCache[geniusLociId] && (Date.now() - lastCacheUpdate) < CACHE_DURATION) {
      return promptsCache[geniusLociId];
    }

    logDebug(`Fetching prompt for genius loci ${geniusLociId} from Firestore...`);
    const docRef = doc(db, 'prompts', geniusLociId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const prompt: DynamicPrompt = {
        id: docSnap.id,
        geniusLociId: data.geniusLociId || docSnap.id,
        initialMessage: data.initialMessage || '',
        context: data.context || '',
        voice: data.voice || '',
        specialties: data.specialties || [],
        bio: data.bio || '',
        quote: data.quote || '',
        personality: data.personality || '',
        avatar: data.avatar || '',
        disableGlobalConcise: data.disableGlobalConcise === true,
        guidelines: data.guidelines || {},
        knowledgeItems: normalizeKnowledgeItems(data.knowledgeItems),
        createdAt: data.createdAt?.toDate(),
        updatedAt: data.updatedAt?.toDate(),
      };
      
      // Aggiorna la cache
      promptsCache[geniusLociId] = prompt;
      
      return prompt;
    } else {
      logDebug(`No prompt found for genius loci ${geniusLociId}`);
      return null;
    }
    
  } catch (error) {
    console.error(`Error fetching prompt for genius loci ${geniusLociId}:`, error);
    return null;
  }
}

// Crea un nuovo prompt
export async function createPrompt(promptData: Omit<DynamicPrompt, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
  try {
    const promptsCol = collection(db, 'prompts');
    const docRef = await addDoc(promptsCol, {
      ...promptData,
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    
    logDebug('Prompt creato con ID:', docRef.id);
    
    // Invalida la cache
    promptsCache = {};
    lastCacheUpdate = 0;
    
    return docRef.id;
  } catch (error) {
    console.error('Errore nella creazione del prompt:', error);
    throw error;
  }
}

// Aggiorna un prompt esistente
export async function updatePrompt(promptId: string, promptData: Partial<Omit<DynamicPrompt, 'id' | 'createdAt'>>): Promise<void> {
  try {
    const promptRef = doc(db, 'prompts', promptId);
    await updateDoc(promptRef, {
      ...promptData,
      updatedAt: new Date(),
    });
    
    logDebug('Prompt aggiornato:', promptId);
    
    // Invalida la cache
    promptsCache = {};
    lastCacheUpdate = 0;
  } catch (error) {
    console.error('Errore nell\'aggiornamento del prompt:', error);
    throw error;
  }
}

// Elimina un prompt
export async function deletePrompt(promptId: string): Promise<void> {
  try {
    const promptRef = doc(db, 'prompts', promptId);
    await deleteDoc(promptRef);
    
    logDebug('Prompt eliminato:', promptId);
    
    // Invalida la cache
    promptsCache = {};
    lastCacheUpdate = 0;
  } catch (error) {
    console.error('Errore nell\'eliminazione del prompt:', error);
    throw error;
  }
}

// Funzione per invalidare la cache
export function invalidatePromptsCache(): void {
  promptsCache = {};
  lastCacheUpdate = 0;
  logDebug('Prompts cache invalidated');
}
